import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiAlertCircle, FiArrowRight, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { API_URLS, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import {
  adminTeamsQuery,
  champFields,
  extractActions,
  extractList,
  normalizeSeasonTeam,
} from '../utilities/tournamentsApi';
import CustomModal from './CustomModal';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/seasonRoster.css';
import '../styles/seasonActions.css';

const emptyReplacement = { previous: '', current: '' };
const emptyPenalty = { teamId: '', score: '', groupId: '' };

const nowActionDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const teamIdOf = (team) => Number(team?.team_id ?? team?.id);
const teamNameOf = (team) => team?.name || team?.short_name || '';

function TeamLogo({ team }) {
  return (
    <img
      src={getTeamImageUrl(team?.image) || defaultTeamLogo}
      alt=""
      onError={(event) => { event.target.src = defaultTeamLogo; }}
    />
  );
}

function TeamCombo({ champId, value, onChange, excludeIds = [] }) {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const blocked = useMemo(() => new Set(excludeIds.map(Number)), [excludeIds]);

  useEffect(() => {
    timerRef.current = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  useEffect(() => {
    if (!value) setSelected(null);
  }, [value]);

  useEffect(() => {
    const q = debounced.trim();
    if (!champId || !q) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    authorizedPost(dispatch, API_URLS.getCatalogTeams, adminTeamsQuery(champId, { search: q, limit: 40, offset: 0 }))
      .then((response) => {
        if (cancelled) return;
        const list = extractList(response.data).map(normalizeSeasonTeam);
        setOptions(list);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debounced, champId, dispatch]);

  const visible = options.filter((team) => !blocked.has(Number(team.team_id)));

  if (selected && !query) {
    return (
      <button type="button" className="sa-picked" onClick={() => onChange('')}>
        <TeamLogo team={selected} />
        <span>{selected.name}</span>
        <FiX />
      </button>
    );
  }

  return (
    <div className="sa-combo">
      <input
        type="text"
        className="st-input"
        value={query}
        placeholder={t('trTeamSearchPh')}
        onChange={(event) => setQuery(event.target.value)}
        autoComplete="off"
      />
      <div className="sa-combo-list">
        {loading ? (
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={24} width={24} />
          </div>
        ) : visible.length === 0 ? (
          <p className="sr-empty-text">{query.trim() ? t('trTeamsNotFound') : t('trTeamSearchStart')}</p>
        ) : visible.map((team) => (
          <button
            type="button"
            key={team.team_id}
            className="sa-combo-row"
            onClick={() => {
              setSelected(team);
              onChange(String(team.team_id));
              setQuery('');
            }}
          >
            <TeamLogo team={team} />
            <span>{team.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SeasonActions({
  leagueId,
  seasonId,
  champId,
  leagueType,
  roster = [],
  groups = [],
  assignment = {},
  onMessage = () => {},
  onSeasonChanged = () => {},
  canWrite = true,
}) {
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formType, setFormType] = useState(null);
  const [replacement, setReplacement] = useState(emptyReplacement);
  const [penalty, setPenalty] = useState(emptyPenalty);
  const [formError, setFormError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);

  const rosterIds = useMemo(() => new Set(roster.map((team) => Number(team.team_id))), [roster]);

  const isGroupTournament = Number(leagueType) === 2;

  const loadActions = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminSeasonActions, {
        ...champFields(champId),
        league_id: Number(leagueId),
        season_id: Number(seasonId),
      });
      setActions(extractActions(response.data));
    } catch (error) {
      setActions([]);
      onMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    loadActions();
  }, [leagueId, seasonId, champId]);

  const closeForm = () => {
    setFormType(null);
    setReplacement(emptyReplacement);
    setPenalty(emptyPenalty);
    setFormError('');
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(lang === 'en' ? 'en-GB' : 'ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatScore = (score) => {
    const value = Number(score);
    const text = value > 0 ? `+${value}` : String(value);
    return t('trPenaltyPoints', { score: text });
  };

  const submitReplacement = async () => {
    const previous = Number(replacement.previous);
    const current = Number(replacement.current);
    if (!previous || !current) {
      setFormError(t('trTeamRequired'));
      return;
    }
    if (!roster.some((team) => Number(team.team_id) === previous)) {
      setFormError(t('trPreviousTeamHint'));
      return;
    }
    if (previous === current) {
      setFormError(t('trSameTeams'));
      return;
    }
    if (rosterIds.has(current)) {
      setFormError(t('trCurrentTeamInSeason'));
      return;
    }

    setSaving(true);
    setFormError('');
    onMessage({ text: '', type: '' });
    try {
      const payload = {
        ...champFields(champId),
        league_id: Number(leagueId),
        season_id: Number(seasonId),
        previous_team: previous,
        current_team: current,
        date: nowActionDate(),
      };
      await authorizedPost(dispatch, API_URLS.addTeamReplacement, payload);
      closeForm();
      onMessage({ text: t('trActionAdded'), type: 'success' });
      await Promise.all([loadActions(true), onSeasonChanged()]);
    } catch (error) {
      onMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const submitPenalty = async () => {
    const teamId = Number(penalty.teamId);
    const score = Number(penalty.score);
    if (!teamId) {
      setFormError(t('trTeamRequired'));
      return;
    }
    if (penalty.score === '' || Number.isNaN(score) || score === 0) {
      setFormError(t('trScoreRequired'));
      return;
    }

    setSaving(true);
    setFormError('');
    onMessage({ text: '', type: '' });
    try {
      const payload = {
        ...champFields(champId),
        league_id: Number(leagueId),
        season_id: Number(seasonId),
        team_id: teamId,
        score,
        date: nowActionDate(),
      };
      if (isGroupTournament && penalty.groupId) {
        const group = groups.find((item) => String(item.id) === String(penalty.groupId));
        payload.group_id = Number(group?.serverId ?? group?.id ?? penalty.groupId);
        payload.group_name = group?.name || null;
      }
      await authorizedPost(dispatch, API_URLS.addTeamPenalty, payload);
      closeForm();
      onMessage({ text: t('trActionAdded'), type: 'success' });
      await loadActions(true);
    } catch (error) {
      onMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const action = pendingDelete;
    if (!action) return;
    setSaving(true);
    onMessage({ text: '', type: '' });
    try {
      const url = action.type === 'replacement'
        ? API_URLS.deleteTeamReplacement
        : API_URLS.deleteTeamPenalty;
      await authorizedPost(dispatch, url, {
        ...champFields(champId),
        id: action.id,
        league_id: Number(leagueId),
        season_id: Number(seasonId),
      });
      setPendingDelete(null);
      onMessage({ text: t('trActionDeleted'), type: 'success' });
      const refresh = [loadActions(true)];
      if (action.type === 'replacement') refresh.push(onSeasonChanged());
      await Promise.all(refresh);
    } catch (error) {
      onMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const teamButton = (team) => {
    if (!team) return <span className="sa-team-name">—</span>;
    const id = teamIdOf(team);
    if (!id) {
      return (
        <span className="sa-team-name">
          <TeamLogo team={team} />
          <span>{teamNameOf(team) || '—'}</span>
        </span>
      );
    }
    return (
      <Link
        to={`/teams_management/${id}`}
        className="sa-team-name"
        title={t('trOpenTeam')}
      >
        <TeamLogo team={team} />
        <span>{teamNameOf(team) || '—'}</span>
      </Link>
    );
  };

  return (
    <section className="st-block sr-block">
      <div className="sr-block-head">
        <div>
          <h2 className="st-block-header">
            {t('trSeasonActions')}
            {!loading ? <span className="sr-count">{actions.length}</span> : null}
          </h2>
          <p className="sr-block-hint">{t('trSeasonActionsHint')}</p>
        </div>
        {canWrite && formType === null ? (
          <div className="sr-toolbar">
            <button
              type="button"
              className="st-btn st-btn-secondary"
              onClick={() => { setFormType('replacement'); setFormError(''); }}
            >
              <FiPlus /> {t('trAddReplacement')}
            </button>
            <button
              type="button"
              className="st-btn st-btn-secondary"
              onClick={() => { setFormType('penalty'); setFormError(''); }}
            >
              <FiPlus /> {t('trAddPenalty')}
            </button>
          </div>
        ) : null}
      </div>

      <div className="sr-block-body">
        {formType === 'replacement' ? (
          <div className="sa-form">
            <div className="sr-note">
              <FiAlertCircle />
              <span>{t('trReplacementHint')}</span>
            </div>
            <div className="st-form-grid">
              <div className="st-field">
                <label>{t('trPreviousTeam')}</label>
                <select
                  className="st-select"
                  value={replacement.previous}
                  onChange={(event) => setReplacement((prev) => ({ ...prev, previous: event.target.value }))}
                >
                  <option value="">{t('trSelectTeam')}</option>
                  {roster.map((team) => (
                    <option key={team.team_id} value={team.team_id}>{team.name}</option>
                  ))}
                </select>
                <span className="sr-block-hint">{t('trPreviousTeamHint')}</span>
              </div>
              <div className="st-field st-field-wide">
                <label>{t('trCurrentTeam')}</label>
                <TeamCombo
                  champId={champId}
                  value={replacement.current}
                  excludeIds={[...rosterIds]}
                  onChange={(value) => setReplacement((prev) => ({ ...prev, current: value }))}
                />
                <span className="sr-block-hint">{t('trCurrentTeamHint')}</span>
              </div>
            </div>
            {formError ? <span className="st-field-error">{formError}</span> : null}
            <div className="sr-inline">
              <button type="button" className="st-btn st-btn-primary" disabled={saving} onClick={submitReplacement}>
                {t('trActionCreate')}
              </button>
              <button type="button" className="st-btn st-btn-secondary" disabled={saving} onClick={closeForm}>
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : null}

        {formType === 'penalty' ? (
          <div className="sa-form">
            <div className="st-form-grid">
              <div className="st-field">
                <label>{t('trPenaltyTeam')}</label>
                <select
                  className="st-select"
                  value={penalty.teamId}
                  onChange={(event) => {
                    const teamId = event.target.value;
                    const raw = teamId != null
                      ? (assignment[Number(teamId)] ?? assignment[teamId])
                      : null;
                    const firstGroup = Array.isArray(raw)
                      ? raw[0]
                      : (raw != null && raw !== '' ? raw : null);
                    const groupId = firstGroup != null ? String(firstGroup) : '';
                    setPenalty((prev) => ({ ...prev, teamId, groupId: isGroupTournament ? groupId : '' }));
                  }}
                >
                  <option value="">{t('trSelectTeam')}</option>
                  {roster.map((team) => (
                    <option key={team.team_id} value={team.team_id}>{team.name}</option>
                  ))}
                </select>
              </div>
              <div className="st-field">
                <label>{t('trPenaltyScore')}</label>
                <input
                  type="number"
                  className="st-input"
                  step="1"
                  value={penalty.score}
                  placeholder={t('trPenaltyScorePh')}
                  onChange={(event) => setPenalty((prev) => ({ ...prev, score: event.target.value }))}
                />
                <span className="sr-block-hint">{t('trPenaltyScoreHint')}</span>
              </div>
              {isGroupTournament ? (
                <div className="st-field">
                  <label>{t('trActionGroup')}</label>
                  <select
                    className="st-select"
                    value={penalty.groupId}
                    onChange={(event) => setPenalty((prev) => ({ ...prev, groupId: event.target.value }))}
                  >
                    <option value="">{t('trActionGroupAuto')}</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                  <span className="sr-block-hint">{t('trActionGroupHint')}</span>
                </div>
              ) : null}
            </div>
            {formError ? <span className="st-field-error">{formError}</span> : null}
            <div className="sr-inline">
              <button type="button" className="st-btn st-btn-primary" disabled={saving} onClick={submitPenalty}>
                {t('trActionCreate')}
              </button>
              <button type="button" className="st-btn st-btn-secondary" disabled={saving} onClick={closeForm}>
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
          </div>
        ) : actions.length === 0 ? (
          <p className="sr-empty-text">{t('trEmptyActions')}</p>
        ) : (
          <div className="sa-list">
            {actions.map((action) => (
              <div key={`${action.type}-${action.id}`} className={`sa-row is-${action.type}`}>
                <span className={`sa-badge is-${action.type}`}>
                  {action.type === 'replacement' ? t('trReplacement') : t('trPenalty')}
                </span>
                <span className="sa-date">{formatDate(action.date) || '—'}</span>
                {action.type === 'replacement' ? (
                  <div className="sa-swap">
                    {teamButton(action.previous_team)}
                    <FiArrowRight />
                    {teamButton(action.current_team)}
                  </div>
                ) : (
                  <div className="sa-penalty">
                    {teamButton(action.team)}
                    <span className={`sa-score ${Number(action.score) < 0 ? 'is-minus' : 'is-plus'}`}>
                      {formatScore(action.score)}
                    </span>
                    {action.group_name ? <span className="sa-group">{action.group_name}</span> : null}
                  </div>
                )}
                {canWrite ? (
                <button
                  type="button"
                  className="sr-icon-btn is-danger"
                  onClick={() => setPendingDelete(action)}
                  title={t('delete')}
                >
                  <FiTrash2 />
                </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={Boolean(pendingDelete)}
        closeModal={() => !saving && setPendingDelete(null)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('trDeleteActionTitle')}</div>
          <p>
            {pendingDelete?.type === 'replacement'
              ? t('trDeleteReplacementText')
              : t('trDeletePenaltyText')}
          </p>
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              disabled={saving}
              onClick={() => setPendingDelete(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-danger-modal"
              disabled={saving}
              onClick={confirmDelete}
            >
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </section>
  );
}
